// The material's lenses, as peers over the material: "Is it grounded?" and
// "Does it teach well?", plus, when it is read against a chat's goals, "Does
// it meet the goals?" spanning the bar under them; each with today's answer
// from the SAME read its lens body renders (so bar and body never disagree).
// Plus the panel's one named step back (`Trail`).

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import {
    type FactCheckViewModel,
    factCheckRead,
    type PanelVerdict,
    verdictOf,
} from "../../../components/FactCheckPanel/FactCheckPanel.js";
import { Icon, type IconName } from "../../../components/Icon/Icon.js";
import {
    type PedagogicalQualityViewModel,
    type PedagogyTone,
    pedagogyRead,
} from "../../../components/PedagogicalQuality/PedagogicalQuality.js";
import { Text } from "../../../components/Text/Text.js";
import "./RightPanel.css";

export type Tone = PedagogyTone;

export const toneOfVerdict = (verdict: PanelVerdict): Tone => {
    switch (verdict) {
        case "findings":
            return "attention";
        case "all_good":
            return "clean";
        case "not_checked":
        case "no_claim_items":
            return "pending";
    }
};

export type LensId = "grounding" | "teaching" | "goal";

// The third lens's answer ("Does it meet the goals?"), when the material is
// read against a chat's goals; absent where there is no chat (Bibliotek).
export type GoalLensBar = { readonly answer: string; readonly tone: Tone };

const LensButton = ({
    icon,
    question,
    answer,
    tone,
    pressed,
    wide = false,
    onClick,
}: {
    readonly wide?: boolean;
    readonly icon: IconName;
    readonly question: string;
    readonly answer: string;
    readonly tone: Tone;
    readonly pressed: boolean;
    readonly onClick: () => void;
}): ReactNode => (
    <button
        type="button"
        className={wide ? "bilda-rp-lens bilda-rp-lens--wide" : "bilda-rp-lens"}
        aria-pressed={pressed}
        onClick={onClick}
    >
        <Icon name={icon} size="sm" />
        <span className="bilda-rp-lens__text">
            <Text as="span" size="sm" weight="medium">
                {question}
            </Text>
            <Text as="span" size="xs" tone="dim">
                {answer}
            </Text>
        </span>
        <span
            className={`bilda-rp-dot bilda-rp-dot--${tone}`}
            aria-hidden="true"
        />
    </button>
);

export const LensBar = ({
    factCheck,
    pedagogy,
    goal,
    active,
    onToggle,
}: {
    readonly factCheck: FactCheckViewModel;
    readonly pedagogy: PedagogicalQualityViewModel;
    readonly goal?: GoalLensBar;
    readonly active: LensId | null;
    readonly onToggle: (lens: LensId) => void;
}): ReactNode => {
    const { t, i18n } = useLingui();
    const ped = pedagogyRead(pedagogy);
    return (
        <fieldset
            className="bilda-rp-lensbar"
            aria-label={t`Review the material`}
        >
            <LensButton
                icon="anchor"
                question={t`Is it grounded?`}
                answer={factCheckRead(factCheck)
                    .headlines.map((h) => i18n._(h))
                    .join(" · ")}
                tone={toneOfVerdict(verdictOf(factCheck))}
                pressed={active === "grounding"}
                onClick={() => onToggle("grounding")}
            />
            <LensButton
                icon="objective"
                question={t`Does it teach well?`}
                answer={i18n._(ped.headline)}
                tone={ped.tone}
                pressed={active === "teaching"}
                onClick={() => onToggle("teaching")}
            />
            {goal === undefined ? null : (
                <LensButton
                    icon="star"
                    question={t`Does it meet the goals?`}
                    answer={goal.answer}
                    tone={goal.tone}
                    pressed={active === "goal"}
                    wide
                    onClick={() => onToggle("goal")}
                />
            )}
        </fieldset>
    );
};

// The way back from a drilled-in view: one step, named by where it returns.
export const Trail = ({
    label,
    onBack,
}: {
    readonly label: string;
    readonly onBack: () => void;
}): ReactNode => (
    <button type="button" className="bilda-rp-trail" onClick={onBack}>
        <Icon name="chevron-left" size="xs" />
        <Text as="span" size="sm" tone="dim" className="bilda-rp-title">
            {label}
        </Text>
    </button>
);
